// components/admin/PlanModal.js
import React, { useState, useEffect } from "react";
import { Modal } from "antd";
import api from "../../../core/axios/axiosInstance";
import { showToast } from "../../../core/data/redux/slices/ToastSlice";
import { useDispatch } from "react-redux";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Sortable Item Component for drag and drop
const SortableFeatureItem = ({
  feature,
  index,
  isEditing,
  editingData,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  onChange,
  loading,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: index });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="d-flex align-items-center mb-2 p-2 border rounded"
    >
      {/* Drag Handle */}
      <span
        {...attributes}
        {...listeners}
        className="me-2 text-muted"
        style={{ cursor: "grab" }}
        title="Drag to reorder"
      >
        <i className="fa fa-grip-vertical" />
      </span>

      {isEditing ? (
        <>
          {/* Editing Mode */}
          <span
            className={`me-2 ${
              editingData.isAvailable ? "text-success" : "text-danger"
            }`}
          >
            <i
              className={`fa fa-${editingData.isAvailable ? "check" : "times"}`}
            />
          </span>
          <input
            type="text"
            className="form-control form-control-sm me-2"
            name="text"
            value={editingData.text}
            onChange={onChange}
            disabled={loading}
            autoFocus
          />
          <div className="form-check form-switch me-2">
            <input
              type="checkbox"
              className="form-check-input"
              name="isAvailable"
              checked={editingData.isAvailable}
              onChange={onChange}
              disabled={loading}
              id={`editAvailable-${index}`}
            />
            <label
              className="form-check-label small"
              htmlFor={`editAvailable-${index}`}
            >
              Available
            </label>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-success me-1"
            onClick={onSave}
            disabled={loading || !editingData.text.trim()}
          >
            <i className="fa fa-check" />
          </button>
          <button
            type="button"
            className="btn btn-sm btn-secondary me-1"
            onClick={onCancel}
            disabled={loading}
          >
            <i className="fa fa-times" />
          </button>
        </>
      ) : (
        <>
          {/* View Mode */}
          <span
            className={`me-2 ${
              feature.isAvailable ? "text-success" : "text-danger"
            }`}
          >
            <i className={`fa fa-${feature.isAvailable ? "check" : "times"}`} />
          </span>
          <span className="flex-grow-1">{feature.text}</span>
          <button
            type="button"
            className="btn btn-sm btn-primary me-1"
            onClick={() => onEdit(index)}
            disabled={loading}
            title="Edit feature"
          >
            <i className="fa fa-edit" />
          </button>
          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={() => onDelete(index)}
            disabled={loading}
            title="Delete feature"
          >
            <i className="fa fa-trash" />
          </button>
        </>
      )}
    </div>
  );
};

const PlanModal = ({ open, onCancel, onSuccess, plan, type }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    billingPeriods: [{ period: "month", price: "" }],
    description: "",
    shortDescription: "",
    features: [],
    isPopular: false,
    isActive: true,
  });

  const [newFeature, setNewFeature] = useState({
    text: "",
    isAvailable: true,
    isHighlighted: false,
  });

  const [editingFeatureIndex, setEditingFeatureIndex] = useState(null);
  const [editingFeatureData, setEditingFeatureData] = useState(null);

  const dispatch = useDispatch();

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (type === "edit" && plan) {
      // Sort features by order when loading
      const sortedFeatures = plan.features
        ? [...plan.features].sort((a, b) => (a.order || 0) - (b.order || 0))
        : [];

      // Convert stripePriceIds to billingPeriods format
      const billingPeriods =
        plan.stripePriceIds?.length > 0
          ? plan.stripePriceIds.map((sp) => ({
              period: sp?.billingPeriod,
              price: (sp.price / 100).toFixed(2),
            }))
          : [{ period: "month", price: "" }];

      setFormData({
        name: plan.name || "",
        billingPeriods: billingPeriods,
        description: plan.description || "",
        shortDescription: plan.shortDescription || "",
        features: sortedFeatures,
        isPopular: plan.isPopular || false,
        isActive: plan.isActive !== undefined ? plan.isActive : true,
      });
    } else {
      // Reset form for add
      setFormData({
        name: "",
        billingPeriods: [{ period: "month", price: "" }],
        description: "",
        shortDescription: "",
        features: [],
        isPopular: false,
        isActive: true,
      });
    }
    // Reset editing state when modal opens/closes
    setEditingFeatureIndex(null);
    setEditingFeatureData(null);
  }, [plan, type, open]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleBillingPeriodChange = (index, field, value) => {
    setFormData((prev) => {
      const newBillingPeriods = [...prev.billingPeriods];
      newBillingPeriods[index] = {
        ...newBillingPeriods[index],
        [field]: value,
      };
      return { ...prev, billingPeriods: newBillingPeriods };
    });
  };

  const addBillingPeriod = () => {
    const existingPeriods = formData.billingPeriods.map((bp) => bp.period);
    const availablePeriods = ["week", "month", "year"].filter(
      (p) => !existingPeriods.includes(p)
    );

    if (availablePeriods.length > 0) {
      setFormData((prev) => ({
        ...prev,
        billingPeriods: [
          ...prev.billingPeriods,
          { period: availablePeriods[0], price: "" },
        ],
      }));
    }
  };

  const removeBillingPeriod = (index) => {
    if (formData.billingPeriods.length > 1) {
      setFormData((prev) => ({
        ...prev,
        billingPeriods: prev.billingPeriods.filter((_, i) => i !== index),
      }));
    }
  };

  const handleFeatureChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewFeature((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const addFeature = () => {
    if (newFeature.text.trim()) {
      setFormData((prev) => ({
        ...prev,
        features: [
          ...prev.features,
          { ...newFeature, order: prev.features.length },
        ],
      }));
      setNewFeature({ text: "", isAvailable: true, isHighlighted: false });
    }
  };

  const removeFeature = (index) => {
    setFormData((prev) => {
      const newFeatures = prev.features.filter((_, i) => i !== index);
      // Reorder remaining features
      return {
        ...prev,
        features: newFeatures.map((f, i) => ({ ...f, order: i })),
      };
    });
    // Clear editing state if we're deleting the feature being edited
    if (editingFeatureIndex === index) {
      setEditingFeatureIndex(null);
      setEditingFeatureData(null);
    }
  };

  const startEditFeature = (index) => {
    setEditingFeatureIndex(index);
    setEditingFeatureData({ ...formData.features[index] });
  };

  const cancelEditFeature = () => {
    setEditingFeatureIndex(null);
    setEditingFeatureData(null);
  };

  const saveEditFeature = () => {
    if (editingFeatureData && editingFeatureData.text.trim()) {
      setFormData((prev) => {
        const newFeatures = [...prev.features];
        newFeatures[editingFeatureIndex] = editingFeatureData;
        return { ...prev, features: newFeatures };
      });
      setEditingFeatureIndex(null);
      setEditingFeatureData(null);
    }
  };

  const handleEditFeatureChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingFeatureData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setFormData((prev) => {
        const oldIndex = prev.features.findIndex((_, i) => i === active.id);
        const newIndex = prev.features.findIndex((_, i) => i === over.id);

        const reorderedFeatures = arrayMove(prev.features, oldIndex, newIndex);
        // Update order field for all features
        return {
          ...prev,
          features: reorderedFeatures.map((f, i) => ({ ...f, order: i })),
        };
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate billing periods
      for (const bp of formData.billingPeriods) {
        if (
          !bp.price ||
          isNaN(parseFloat(bp.price)) ||
          parseFloat(bp.price) < 0
        ) {
          throw new Error(`Invalid price for ${bp.period} period`);
        }
      }

      // Validate price relationships using priority scores
      // Higher score = longer/more valuable period
      const periodPriorityMap = {
        day: 1,
        week: 2,
        month: 3,
        year: 4,
        annual: 4, // alias for year
      };

      const periodPrices = {};
      formData.billingPeriods.forEach((bp) => {
        periodPrices[bp.period] = parseFloat(bp.price);
      });

      // Check that shorter periods (lower priority) don't cost more than longer periods (higher priority)
      const periodsWithPrices = formData.billingPeriods.map((bp) => ({
        period: bp.period,
        price: parseFloat(bp.price),
        priority: periodPriorityMap[bp.period] || 0,
      }));

      for (let i = 0; i < periodsWithPrices.length; i++) {
        for (let j = 0; j < periodsWithPrices.length; j++) {
          if (i !== j) {
            const period1 = periodsWithPrices[i];
            const period2 = periodsWithPrices[j];

            // If period1 has lower priority (shorter) but higher price than period2 (longer)
            if (
              period1.priority < period2.priority &&
              period1.price > period2.price
            ) {
              const periodLabels = {
                day: "Daily",
                week: "Weekly",
                month: "Monthly",
                year: "Yearly",
                annual: "Yearly",
              };
              throw new Error(
                `${
                  periodLabels[period1.period] || period1.period
                } price cannot be greater than ${
                  periodLabels[period2.period] || period2.period
                } price`
              );
            }
          }
        }
      }

      // Convert prices to cents and prepare billing periods
      const billingPeriods = formData.billingPeriods.map((bp) => ({
        period: bp.period,
        price: Math.round(parseFloat(bp.price) * 100), // Convert to cents
      }));

      const payload = {
        name: formData.name,
        billingPeriods: billingPeriods,
        description: formData.description,
        features: formData.features,
        isPopular: formData.isPopular,
        isActive: formData.isActive,
      };

      console.log("Submitting payload:", payload);

      if (type === "add") {
        await api.post("/admin/plans", payload);
        dispatch(
          showToast({
            heading: "Success",
            message: "Plan created successfully!",
            variant: "success",
          })
        );
      } else {
        await api.put(`/admin/plans/${plan._id}`, payload);
        dispatch(
          showToast({
            heading: "Success",
            message: "Plan updated successfully!",
            variant: "success",
          })
        );
      }

      onSuccess();
    } catch (error) {
      console.error("Error saving plan obj:", error?.toString());
      dispatch(
        showToast({
          heading: "Error",
          message: error?.toString() || "Failed to save plan",
          variant: "danger",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const periodLabels = {
    week: "Weekly",
    month: "Monthly",
    year: "Yearly",
  };

  const existingPeriods = formData.billingPeriods.map((bp) => bp.period);
  const canAddMorePeriods = existingPeriods.length < 3;

  return (
    <Modal
      title={type === "add" ? "Add New Plan" : "Edit Plan"}
      open={open}
      onCancel={onCancel}
      width={900}
      centered
      footer={null}
    >
      <form onSubmit={handleSubmit}>
        <div
          style={{ maxHeight: "75vh", overflowY: "auto", overflowX: "hidden" }}
        >
          <div className="row gy-3">
            {/* Plan Name */}
            <div className="col-md-12">
              <div className="form-group">
                <label className="form-label">Plan Name *</label>
                <input
                  type="text"
                  className="form-control"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={
                    loading ||
                    (type === "edit" &&
                      (plan?.name === "Starter" || plan?.name === "Pro"))
                  }
                  title={
                    type === "edit" &&
                    (plan?.name === "Starter" || plan?.name === "Pro")
                      ? `Cannot edit the name of ${plan?.name} plan - this is a protected plan`
                      : ""
                  }
                />
                {type === "edit" &&
                  (plan?.name === "Starter" || plan?.name === "Pro") && (
                    <small className="text-muted">
                      Note: The name of {plan?.name} plan cannot be modified as
                      it's a protected plan.
                    </small>
                  )}
              </div>
            </div>

            {/* Billing Periods Section */}
            <div className="col-md-12">
              <div className="form-group">
                <span className="form-label d-flex justify-content-between align-items-center">
                  <span>Billing Periods * (at least one required)</span>
                  {canAddMorePeriods && (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary "
                      onClick={addBillingPeriod}
                      disabled={loading}
                    >
                      <i className="fa fa-plus me-1" />
                      Add Billing Period
                    </button>
                  )}
                </span>
                {type === "edit" && (
                  <div
                    className="alert alert-warning py-2 px-3 mb-2 d-flex align-items-start"
                    role="alert"
                  >
                    <i className="fa fa-exclamation-triangle me-2 mt-1"></i>
                    <small>
                      <strong>Warning:</strong> Modifying prices or removing
                      billing periods will automatically cancel all active
                      subscriptions using those pricing schedules. Users will
                      need to resubscribe with the new pricing.
                    </small>
                  </div>
                )}
                <div className="border rounded p-3">
                  {formData.billingPeriods.map((bp, index) => (
                    <div key={index} className="row mb-3 align-items-end">
                      <div className="col-md-5">
                        <label className="form-label small">Period</label>
                        <select
                          className="form-select"
                          value={bp.period}
                          onChange={(e) =>
                            handleBillingPeriodChange(
                              index,
                              "period",
                              e.target.value
                            )
                          }
                          disabled={loading}
                          required
                        >
                          {["week", "month", "year"].map((period) => (
                            <option
                              key={period}
                              value={period}
                              disabled={
                                existingPeriods.includes(period) &&
                                bp.period !== period
                              }
                            >
                              {periodLabels[period]}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-5">
                        <label className="form-label small">
                          Price (AED) *
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={bp.price}
                          onChange={(e) =>
                            handleBillingPeriodChange(
                              index,
                              "price",
                              e.target.value
                            )
                          }
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          disabled={loading}
                          required
                        />
                      </div>
                      <div className="col-md-1">
                        {formData.billingPeriods.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-danger w-100"
                            onClick={() => removeBillingPeriod(index)}
                            disabled={loading}
                            title="Remove billing period"
                          >
                            <i className="fa fa-trash" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {formData.billingPeriods.length === 0 && (
                    <div className="text-muted text-center py-2">
                      No billing periods added. At least one is required.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="col-md-12">
              <div className="form-group">
                <label className="form-label">Description *</label>
                <textarea
                  className="form-control"
                  name="description"
                  rows="3"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {/* Features Section */}
            <div className="col-md-12 mt-3">
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0">Features</h6>
                </div>
                <div className="card-body">
                  <div className="row align-items-end mb-3">
                    {/* Feature Text */}
                    <div className="col-md-7">
                      <label className="form-label">Feature Text</label>
                      <div className="d-flex align-items-baseline gap-3">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter feature description"
                          name="text"
                          value={newFeature.text}
                          onChange={handleFeatureChange}
                          disabled={loading}
                        />
                        <div className="form-check form-switch d-flex align-items-start mt-2">
                          <input
                            type="checkbox"
                            className="form-check-input me-2 "
                            name="isAvailable"
                            checked={newFeature.isAvailable}
                            onChange={handleFeatureChange}
                            disabled={loading}
                            id="featureAvailable"
                          />
                          <label
                            className="form-check-label mb-0"
                            htmlFor="featureAvailable"
                          >
                            Available
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Checkbox */}

                    {/* Add Button */}
                    <div className="col-2 ms-auto">
                      <button
                        type="button"
                        className="btn btn-primary w-100"
                        onClick={addFeature}
                        disabled={loading || !newFeature.text.trim()}
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  <div className="features-list">
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={formData.features.map((_, index) => index)}
                        strategy={verticalListSortingStrategy}
                      >
                        {formData.features.map((feature, index) => (
                          <SortableFeatureItem
                            key={index}
                            feature={feature}
                            index={index}
                            isEditing={editingFeatureIndex === index}
                            editingData={editingFeatureData}
                            onEdit={startEditFeature}
                            onSave={saveEditFeature}
                            onCancel={cancelEditFeature}
                            onDelete={removeFeature}
                            onChange={handleEditFeatureChange}
                            loading={loading}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  </div>
                </div>
              </div>
            </div>

            {/* Popular Plan Toggle */}
            <div className="col-md-6 mt-3">
              <div className="form-check form-switch">
                <input
                  type="checkbox"
                  className="form-check-input"
                  name="isPopular"
                  checked={formData.isPopular}
                  onChange={handleInputChange}
                  disabled={loading}
                  role="switch"
                />
                <label className="form-check-label">Mark as Popular Plan</label>
              </div>
            </div>

            {/* Active Status Toggle */}
            <div className="col-md-6 mt-3">
              <div className="form-check form-switch">
                <input
                  type="checkbox"
                  className="form-check-input"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  disabled={
                    loading ||
                    (type === "edit" &&
                      (plan?.name === "Starter" || plan?.name === "Pro"))
                  }
                  role="switch"
                />
                <label className="form-check-label">Active Plan</label>
              </div>
              {type === "edit" &&
                (plan?.name === "Starter" || plan?.name === "Pro") && (
                  <small className="text-muted">
                    Note: The active status of {plan?.name} plan cannot be
                    modified as it's a protected plan.
                  </small>
                )}
            </div>
          </div>
        </div>
        <div className="mt-3 d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={loading}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                {type === "add" ? "Creating..." : "Updating..."}
              </>
            ) : type === "add" ? (
              "Create Plan"
            ) : (
              "Update Plan"
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default PlanModal;
